import React from "react";

/**
 * MafiaTheme — ONE shared stylesheet for the whole MAFIA Aux module.
 *
 * Why this exists: the per-screen styling drifted (different button sizes,
 * font scales, pill shapes, table looks) because each screen carried its own
 * CSS. This file is the single source of truth. Every Mafia screen wraps its
 * content in a `.mafia-screen` element and renders <MafiaTheme/> once; all the
 * shared rules below then apply uniformly. Change a value here → every screen
 * updates together.
 *
 * Scope: rules are namespaced under `.mafia-screen` so they NEVER leak into
 * other modules (DI, EPO, DMS, HR, …). No global/theme/shared-kit changes.
 *
 * Design language: keeps the modern Velzon theme (Inter font, indigo/teal,
 * rounded cards) but enforces the legacy app's best quality — uniformity:
 * one button size, one font scale, one table style, one dialog/panel layout.
 */
const CSS = `
/* ---- tokens (one place to tune the whole module) ---- */
.mafia-screen{
  --m-font: 'Inter', system-ui, -apple-system, "Segoe UI", sans-serif;
  --m-ink:#1f2733; --m-muted:#878a99; --m-line:#e9ebec; --m-soft:#f3f6f9;
  --m-primary:#4f51c9; --m-primary-d:#3f41b0;
  --m-radius:0.5rem; --m-radius-lg:0.875rem;
  --m-btn-h:38px; --m-btn-fs:0.8125rem; --m-btn-px:0.9rem;
  --m-label-fs:0.6875rem; --m-cell-fs:0.8125rem; --m-head-fs:0.6875rem;
  font-family: var(--m-font);
  color: var(--m-ink);
}
.mafia-screen, .mafia-screen * { font-family: var(--m-font); }

/* ---- buttons: ONE size + shape everywhere ---- */
.mafia-screen .btn{
  font-family: var(--m-font);
  font-size: var(--m-btn-fs);
  font-weight: 500;
  min-height: var(--m-btn-h);
  padding: 0 var(--m-btn-px);
  border-radius: var(--m-radius);
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  line-height: 1; white-space: nowrap;
}
.mafia-screen .btn-sm{ min-height: 32px; font-size: 0.78rem; padding: 0 0.7rem; }
.mafia-screen .btn i{ font-size: 1rem; }

/* ---- info pills / status chips: ONE look (kills the mismatched chips) ---- */
.mafia-screen .m-pill{
  display: inline-flex; align-items: center; gap: 0.5rem;
  min-height: var(--m-btn-h); padding: 0.3rem 0.75rem;
  border: 1px solid var(--m-line); border-radius: var(--m-radius);
  background: var(--m-soft); max-width: 320px;
}
.mafia-screen .m-pill-label,
.mafia-screen .all-parts-info-label,
.mafia-screen .frm-job-info-label{
  font-size: var(--m-label-fs); font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.05em; color: var(--m-muted); white-space: nowrap; line-height: 1.1;
}
.mafia-screen .m-pill-value,
.mafia-screen .all-parts-info-value,
.mafia-screen .frm-job-info-value{
  font-size: var(--m-cell-fs); font-weight: 600; color: #495057;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* normalize the existing per-screen pills to the shared shape/size */
.mafia-screen .all-parts-info-pill,
.mafia-screen .frm-job-info-pill,
.mafia-screen .all-parts-queue-badge{
  min-height: var(--m-btn-h) !important;
  border-radius: var(--m-radius) !important;
  border: 1px solid var(--m-line) !important;
  background: var(--m-soft) !important;
  gap: 0.5rem !important;
}

/* ---- toolbar band: one clean row, consistent gap ---- */
.mafia-screen .m-toolbar{
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem;
  margin-bottom: 0.9rem;
}
.mafia-screen .m-toolbar .m-spacer{ flex: 1; }

/* ---- tables: ONE style across All/Unchecked/Unreleased/Edit/Print ---- */
.mafia-screen table thead th,
.mafia-screen .table thead th{
  font-size: var(--m-head-fs); font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.04em; color: #5a6172; background: #f5f6fb;
  border-bottom: 2px solid var(--m-line); white-space: nowrap; vertical-align: middle;
}
.mafia-screen table tbody td,
.mafia-screen .table tbody td{
  font-size: var(--m-cell-fs); color: var(--m-ink); vertical-align: middle;
}
.mafia-screen .table-hover tbody tr:hover td{ background: #f8f9ff; }
.mafia-screen tbody tr.selected td,
.mafia-screen tbody tr.table-active td{ background: #eef0ff !important; }

/* ---- cards / panels / dialogs: consistent radius + header ---- */
.mafia-screen .card{ border-radius: var(--m-radius-lg); }
.mafia-screen .m-panel-title{
  font-size: 0.95rem; font-weight: 600; color: var(--m-ink); margin: 0 0 0.25rem;
}
.mafia-screen .m-panel-sub{ font-size: 0.8rem; color: var(--m-muted); margin: 0 0 0.9rem; }
/* dialog action row: buttons grouped bottom-right, consistent gap */
.mafia-screen .m-dialog-actions{
  display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1rem;
}

/* ---- form fields: consistent height/scale ---- */
.mafia-screen .form-control, .mafia-screen .form-select{
  font-size: var(--m-cell-fs); border-radius: var(--m-radius);
}
.mafia-screen label, .mafia-screen .form-label{
  font-size: 0.8rem; font-weight: 500; color: #495057;
}

/* ---- search field: consistent across screens ---- */
.mafia-screen .m-search .input-group-text{ background: #fff; border-right: 0; }
.mafia-screen .m-search .form-control{ border-left: 0; }
`;

const MafiaTheme = () => <style>{CSS}</style>;

export default MafiaTheme;
